import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import {
  Loader2, LogOut, RefreshCw, FileImage, CheckCircle2, Clock, ChevronDown, ChevronUp, ShieldCheck,
  Download, Sun, Moon, Search, Pencil, Trash2, Users, BadgeCheck, BedDouble, IndianRupee,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useTheme } from "@/hooks/useTheme";
import { EditRegistration } from "@/components/EditRegistration";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const TOKEN_KEY = "gc_admin_token";
const ROLE_KEY = "gc_admin_role";

const Detail = ({ label, value }) => (
  <div>
    <p className="text-[11px] uppercase tracking-wider text-white/40">{label}</p>
    <p className="mt-0.5 text-sm text-white/90">{value || "—"}</p>
  </div>
);

export default function AdminPage() {
  const [theme, toggleTheme] = useTheme();
  const [token, setToken] = useState(() => sessionStorage.getItem(TOKEN_KEY) || "");
  const [role, setRole] = useState(() => sessionStorage.getItem(ROLE_KEY) || "");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loginError, setLoginError] = useState("");
  const [loggingIn, setLoggingIn] = useState(false);
  const [registrations, setRegistrations] = useState(null);
  const [loadError, setLoadError] = useState("");
  const [expanded, setExpanded] = useState({});
  const [updating, setUpdating] = useState("");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [editing, setEditing] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState("");

  const logout = useCallback(() => {
    sessionStorage.removeItem(TOKEN_KEY);
    sessionStorage.removeItem(ROLE_KEY);
    setToken("");
    setRole("");
  }, []);

  const fetchRegistrations = useCallback(async (tok) => {
    try {
      const res = await axios.get(`${API}/admin/registrations`, {
        headers: { Authorization: `Bearer ${tok}` },
      });
      setRegistrations(res.data.registrations);
      setLoadError("");
    } catch (err) {
      if (err.response?.status === 401) {
        logout();
      } else {
        setLoadError("Could not load registrations. Please try again.");
      }
    }
  }, [logout]);

  useEffect(() => {
    if (token) fetchRegistrations(token);
  }, [token, fetchRegistrations]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoggingIn(true);
    setLoginError("");
    try {
      const res = await axios.post(`${API}/admin/login`, { username, password });
      sessionStorage.setItem(TOKEN_KEY, res.data.token);
      sessionStorage.setItem(ROLE_KEY, res.data.role);
      setRole(res.data.role);
      setToken(res.data.token);
    } catch (err) {
      setLoginError(err.response?.data?.detail || "Login failed");
    } finally {
      setLoggingIn(false);
    }
  };

  const toggleVerified = async (reg) => {
    setUpdating(reg.registration_id);
    try {
      await axios.patch(
        `${API}/admin/registrations/${reg.registration_id}`,
        { payment_verified: !reg.payment_verified },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setRegistrations((rs) =>
        rs.map((r) => (r.registration_id === reg.registration_id ? { ...r, payment_verified: !r.payment_verified } : r))
      );
    } catch {
      setLoadError("Update failed — please try again.");
    } finally {
      setUpdating("");
    }
  };

  const exportCsv = () => {
    if (!registrations) return;
    const headers = [
      "Reference", "Submitted", "Language", "Full Name", "Gender", "Age", "Date of Birth",
      "Mobile", "Email", "Address", "State", "Country", "Church / Organization", "Designation",
      "Reference / Heard via", "Attending as family", "Family members", "Payment completed",
      "Payment verified", "From outside Bangalore", "Needs accommodation", "Accommodation option", "Stay proof uploaded", "Arrival", "Departure",
      "Team email sent",
    ];
    const rows = registrations.map((r) => [
      r.registration_id,
      r.created_at ? new Date(r.created_at).toLocaleString() : "",
      r.language === "kn" ? "Kannada" : "English",
      r.personal?.full_name, r.personal?.gender, r.personal?.age, r.personal?.dob,
      r.personal?.mobile, r.personal?.email, r.personal?.address, r.personal?.state, r.personal?.country,
      r.church?.church_name,
      r.church?.designation === "Other" ? `Other: ${r.church?.designation_other || ""}` : r.church?.designation,
      r.church?.reference,
      r.family?.attending_as_family,
      (r.family?.members || []).map((m) => `${m.name} (${m.relation}, ${m.age}, ${m.gender})`).join("; "),
      r.payment?.payment_completed,
      r.payment_verified ? "verified" : "pending",
      r.travel?.from_outside, r.travel?.needs_accommodation,
      r.travel?.needs_accommodation === "yes"
        ? (r.travel?.accommodation_type === "dormitory" ? "In-Campus Dormitory" : r.travel?.accommodation_type === "hotel" ? "Outside Hotel/Airbnb" : "")
        : "",
      r.payment?.stay_proof_path ? "yes" : "no",
      (r.travel?.arrival || "").replace("T", " "), (r.travel?.departure || "").replace("T", " "),
      r.email_sent ? "yes" : "no",
    ]);
    const csv = [headers, ...rows]
      .map((row) => row.map((v) => `"${String(v ?? "").replace(/"/g, '""')}"`).join(","))
      .join("\r\n");
    const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `glory-conference-registrations-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const viewProof = async (path) => {
    const fname = path?.split("/").pop();
    if (!fname) return;
    try {
      const res = await axios.get(`${API}/admin/payment-proof/${fname}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: "blob",
      });
      const url = URL.createObjectURL(new Blob([res.data], { type: res.headers["content-type"] }));
      window.open(url, "_blank");
    } catch {
      setLoadError("Could not open the payment proof.");
    }
  };

  const deleteRegistration = async (reg) => {
    setUpdating(reg.registration_id);
    try {
      await axios.delete(`${API}/admin/registrations/${reg.registration_id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setRegistrations((rs) => rs.filter((r) => r.registration_id !== reg.registration_id));
      setConfirmDelete("");
    } catch (err) {
      setLoadError(err.response?.data?.detail || "Delete failed — please try again.");
    } finally {
      setUpdating("");
    }
  };

  const filtered = useMemo(() => {
    if (!registrations) return null;
    const q = search.trim().toLowerCase();
    return registrations.filter((r) => {
      if (statusFilter === "verified" && !r.payment_verified) return false;
      if (statusFilter === "pending" && r.payment_verified) return false;
      if (!q) return true;
      return [r.registration_id, r.personal?.full_name, r.personal?.mobile, r.personal?.email, r.church?.church_name]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q));
    });
  }, [registrations, search, statusFilter]);

  const stats = useMemo(() => {
    if (!registrations) return null;
    return {
      total: registrations.length,
      candidates: registrations.reduce((n, r) => n + 1 + (r.family?.members?.length || 0), 0),
      verified: registrations.filter((r) => r.payment_verified).length,
      dormitory: registrations.filter((r) => r.travel?.accommodation_type === "dormitory").length,
    };
  }, [registrations]);

  /* ---------------- login ---------------- */
  if (!token) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#0B1233] px-4">
        <form data-testid="admin-login-form" onSubmit={handleLogin}
          className="w-full max-w-sm rounded-3xl border border-white/10 bg-white/[0.04] p-8">
          <ShieldCheck className="mx-auto h-10 w-10 text-[#FFE500]" />
          <h1 data-testid="admin-login-title" className="mt-4 text-center font-display text-2xl font-bold uppercase tracking-wide text-white">
            Team Sign In
          </h1>
          <p className="mt-1 text-center text-xs text-white/40">Glory Conference Bangalore — Registrations</p>
          <div className="mt-7 space-y-4">
            <div>
              <Label className="text-xs text-white/60">Username</Label>
              <Input data-testid="admin-username-input" type="text" required value={username} onChange={(e) => setUsername(e.target.value)}
                className="mt-1 h-11 border-white/15 bg-white/5 text-white" autoComplete="username" />
            </div>
            <div>
              <Label className="text-xs text-white/60">Password</Label>
              <Input data-testid="admin-password-input" type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                className="mt-1 h-11 border-white/15 bg-white/5 text-white" autoComplete="current-password" />
            </div>
            {loginError && <p data-testid="admin-login-error" className="text-sm text-red-400">{loginError}</p>}
            <Button data-testid="admin-login-btn" type="submit" disabled={loggingIn}
              className="h-11 w-full rounded-full bg-[#FFE500] font-display font-bold uppercase tracking-wider text-[#0B1233] hover:bg-[#FFE500]/90">
              {loggingIn ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sign In"}
            </Button>
          </div>
        </form>
      </div>
    );
  }

  /* ---------------- dashboard ---------------- */
  return (
    <div className="min-h-screen bg-[#0B1233] px-4 py-8 sm:px-6">
      <div className="mx-auto max-w-6xl">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 data-testid="admin-title" className="font-display text-2xl font-bold uppercase tracking-wide text-white sm:text-3xl">
              Registrations
            </h1>
            <p data-testid="admin-count" className="mt-1 text-sm text-[#FFE500]">
              {registrations ? `${registrations.length} total` : "Loading…"}
            </p>
            <span data-testid="admin-role-badge" className={`mt-2 inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[11px] font-bold uppercase tracking-wider ${
              role === "super_admin" ? "bg-[#FFE500] text-[#0B1233]" : "border border-[#FFE500]/40 text-[#FFE500]"
            }`}>
              <ShieldCheck className="h-3 w-3" /> {role === "super_admin" ? "Super Admin" : "Team"}
            </span>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button data-testid="admin-theme-toggle" variant="outline" onClick={toggleTheme}
              aria-label="Toggle theme"
              className="rounded-full border-white/20 bg-transparent px-3 text-white hover:border-[#FFE500]/50 hover:text-[#FFE500]">
              {theme === "light" ? <Moon className="h-4 w-4" /> : <Sun className="h-4 w-4" />}
            </Button>
            <Button data-testid="export-csv-btn" variant="outline" onClick={exportCsv}
              disabled={!registrations || registrations.length === 0}
              className="rounded-full border-[#FFE500]/40 bg-transparent text-[#FFE500] hover:bg-[#FFE500] hover:text-[#0B1233]">
              <Download className="mr-1 h-4 w-4" /> Export CSV
            </Button>
            <Button data-testid="admin-refresh-btn" variant="outline" onClick={() => fetchRegistrations(token)}
              className="rounded-full border-white/20 bg-transparent text-white hover:border-[#FFE500]/50 hover:text-[#FFE500]">
              <RefreshCw className="mr-1 h-4 w-4" /> Refresh
            </Button>
            <Button data-testid="admin-logout-btn" variant="outline"
              onClick={logout}
              className="rounded-full border-white/20 bg-transparent text-white hover:border-red-400 hover:text-red-400">
              <LogOut className="mr-1 h-4 w-4" /> Sign Out
            </Button>
          </div>
        </div>

        {loadError && <p data-testid="admin-load-error" className="mt-4 rounded-xl border border-red-400/50 bg-red-400/10 p-3 text-sm text-red-300">{loadError}</p>}

        {stats && (
          <div data-testid="admin-stats" className="mt-6 grid grid-cols-2 gap-3 lg:grid-cols-4">
            {[
              { icon: Users, label: "Registrations", value: stats.total, testId: "stat-registrations" },
              { icon: BadgeCheck, label: "Total candidates", value: stats.candidates, testId: "stat-candidates" },
              { icon: IndianRupee, label: "Payments verified", value: stats.verified, testId: "stat-verified" },
              { icon: BedDouble, label: "Dormitory stays", value: stats.dormitory, testId: "stat-dormitory" },
            ].map((s) => (
              <div key={s.testId} data-testid={s.testId} className="rounded-2xl border border-white/10 bg-white/[0.03] p-4">
                <s.icon className="h-5 w-5 text-[#FFE500]" />
                <p className="mt-2 font-display text-2xl font-bold text-white">{s.value}</p>
                <p className="text-xs text-white/50">{s.label}</p>
              </div>
            ))}
          </div>
        )}

        {registrations && registrations.length > 0 && (
          <div data-testid="admin-filters" className="mt-5 flex flex-wrap gap-3">
            <div className="relative min-w-[220px] flex-1">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-white/40" />
              <Input data-testid="admin-search-input" value={search} onChange={(e) => setSearch(e.target.value)}
                placeholder="Search name, mobile, reference, church…"
                className="h-11 border-white/15 bg-white/5 pl-9 text-white placeholder:text-white/30" />
            </div>
            <div className="flex rounded-full border border-white/15 bg-white/5 p-1">
              {[["all", "All"], ["pending", "Pending"], ["verified", "Verified"]].map(([v, label]) => (
                <button key={v} type="button" data-testid={`filter-${v}`} onClick={() => setStatusFilter(v)}
                  className={`rounded-full px-4 py-2 text-xs font-bold uppercase tracking-wider transition-all ${
                    statusFilter === v ? "bg-[#FFE500] text-[#0B1233]" : "text-white/60 hover:text-white"
                  }`}>
                  {label}
                </button>
              ))}
            </div>
          </div>
        )}

        {!registrations && (
          <div className="mt-16 flex justify-center"><Loader2 data-testid="admin-loading" className="h-8 w-8 animate-spin text-[#FFE500]" /></div>
        )}

        {registrations && registrations.length === 0 && (
          <p data-testid="admin-empty" className="mt-16 text-center text-white/50">No registrations yet.</p>
        )}

        {filtered && registrations?.length > 0 && filtered.length === 0 && (
          <p data-testid="admin-no-matches" className="mt-16 text-center text-white/50">No registrations match your search.</p>
        )}

        <div className="mt-6 space-y-4">
          {filtered?.map((reg) => {
            const isOpen = expanded[reg.registration_id];
            return (
              <div key={reg.registration_id} data-testid={`reg-card-${reg.registration_id}`}
                className="rounded-2xl border border-white/10 bg-white/[0.03]">
                <button type="button" data-testid={`reg-toggle-${reg.registration_id}`}
                  onClick={() => setExpanded((x) => ({ ...x, [reg.registration_id]: !isOpen }))}
                  className="flex w-full flex-wrap items-center justify-between gap-3 p-5 text-left">
                  <div>
                    <p className="font-display text-lg font-bold tracking-wide text-[#FFE500]">{reg.registration_id}</p>
                    <p className="mt-0.5 text-sm text-white/85">{reg.personal.full_name} · {reg.personal.mobile}</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <span data-testid={`reg-status-${reg.registration_id}`}
                      className={`flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-bold ${
                        reg.payment_verified ? "bg-green-400/15 text-green-400" : "bg-[#FFE500]/15 text-[#FFE500]"
                      }`}>
                      {reg.payment_verified ? <CheckCircle2 className="h-3.5 w-3.5" /> : <Clock className="h-3.5 w-3.5" />}
                      {reg.payment_verified ? "Verified" : "Pending"}
                    </span>
                    {isOpen ? <ChevronUp className="h-5 w-5 text-white/40" /> : <ChevronDown className="h-5 w-5 text-white/40" />}
                  </div>
                </button>

                {isOpen && (
                  <div data-testid={`reg-details-${reg.registration_id}`} className="border-t border-white/10 p-5">
                    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                      <Detail label="Email" value={reg.personal.email} />
                      <Detail label="Gender / Age" value={`${reg.personal.gender} · ${reg.personal.age}`} />
                      <Detail label="Date of Birth" value={reg.personal.dob} />
                      <Detail label="Language" value={reg.language === "kn" ? "Kannada" : "English"} />
                      <Detail label="Address" value={reg.personal.address} />
                      <Detail label="State" value={reg.personal.state} />
                      <Detail label="Country" value={reg.personal.country} />
                      <Detail label="Church / Org" value={reg.church.church_name} />
                      <Detail label="Designation" value={reg.church.designation === "Other" ? `Other: ${reg.church.designation_other}` : reg.church.designation} />
                      <Detail label="Reference" value={reg.church.reference} />
                      <Detail label="Family attending" value={reg.family.attending_as_family} />
                      {reg.family.members?.length > 0 && (
                        <Detail label="Family members" value={reg.family.members.map((m) => `${m.name} (${m.relation}, ${m.age}, ${m.gender})`).join("; ")} />
                      )}
                      <Detail label="Payment completed" value={reg.payment.payment_completed} />
                      <Detail label="From outside Bangalore" value={reg.travel.from_outside} />
                      <Detail label="Needs accommodation" value={reg.travel.needs_accommodation} />
                      <Detail label="Accommodation" value={
                        reg.travel.needs_accommodation === "yes"
                          ? reg.travel.accommodation_type === "dormitory"
                            ? "In-Campus Dormitory (₹1,500/person)"
                            : reg.travel.accommodation_type === "hotel"
                              ? "Outside Hotel / Airbnb"
                              : "—"
                          : "—"
                      } />
                      <Detail label="Arrival" value={reg.travel.arrival?.replace("T", " ")} />
                      <Detail label="Departure" value={reg.travel.departure?.replace("T", " ")} />
                      <Detail label="Submitted" value={new Date(reg.created_at).toLocaleString()} />
                      <Detail label="Team email sent" value={reg.email_sent ? "Yes" : "No"} />
                    </div>
                    <div className="mt-5 flex flex-wrap gap-3">
                      {reg.payment.payment_proof_path && (
                        <Button data-testid={`reg-proof-${reg.registration_id}`} variant="outline"
                          onClick={() => viewProof(reg.payment.payment_proof_path)}
                          className="rounded-full border-[#FFE500]/40 bg-transparent text-[#FFE500] hover:bg-[#FFE500] hover:text-[#0B1233]">
                          <FileImage className="mr-1 h-4 w-4" /> View Payment Proof
                        </Button>
                      )}
                      {reg.payment.stay_proof_path && (
                        <Button data-testid={`reg-stay-proof-${reg.registration_id}`} variant="outline"
                          onClick={() => viewProof(reg.payment.stay_proof_path)}
                          className="rounded-full border-[#FFE500]/40 bg-transparent text-[#FFE500] hover:bg-[#FFE500] hover:text-[#0B1233]">
                          <FileImage className="mr-1 h-4 w-4" /> View Stay Proof
                        </Button>
                      )}
                      <Button data-testid={`reg-verify-${reg.registration_id}`}
                        onClick={() => toggleVerified(reg)}
                        disabled={updating === reg.registration_id}
                        className={`rounded-full font-bold ${
                          reg.payment_verified
                            ? "bg-white/10 text-white hover:bg-white/20"
                            : "bg-[#FFE500] text-[#0B1233] hover:bg-[#FFE500]/90"
                        }`}>
                        {updating === reg.registration_id ? <Loader2 className="h-4 w-4 animate-spin" />
                          : reg.payment_verified ? "Mark as Pending" : "Mark Payment Verified"}
                      </Button>
                      <Button data-testid={`reg-edit-${reg.registration_id}`} variant="outline"
                        onClick={() => setEditing(reg)}
                        className="rounded-full border-white/25 bg-transparent text-white hover:border-[#FFE500]/60 hover:text-[#FFE500]">
                        <Pencil className="mr-1 h-4 w-4" /> Edit
                      </Button>
                      {role === "super_admin" && (
                        confirmDelete === reg.registration_id ? (
                          <Button data-testid={`reg-delete-confirm-${reg.registration_id}`}
                            onClick={() => deleteRegistration(reg)}
                            disabled={updating === reg.registration_id}
                            className="rounded-full bg-red-500 font-bold text-white hover:bg-red-600">
                            {updating === reg.registration_id ? <Loader2 className="h-4 w-4 animate-spin" /> : "Confirm delete?"}
                          </Button>
                        ) : (
                          <Button data-testid={`reg-delete-${reg.registration_id}`} variant="outline"
                            onClick={() => setConfirmDelete(reg.registration_id)}
                            className="rounded-full border-red-400/40 bg-transparent text-red-400 hover:bg-red-500 hover:text-white">
                            <Trash2 className="mr-1 h-4 w-4" /> Delete
                          </Button>
                        )
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {editing && (
        <EditRegistration
          reg={editing}
          token={token}
          onClose={() => setEditing(null)}
          onSaved={(updated) => {
            setRegistrations((rs) => rs.map((r) => (r.registration_id === updated.registration_id ? updated : r)));
            setEditing(null);
          }}
        />
      )}
    </div>
  );
}
